import axiosHttp from "services/axiosHttp";
import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import { BACKOFFICE_ORDER_REQUEST } from "../constants/apiPaths";

/**
 * This list only ever shows genuine "order requests" — pending requests awaiting placement, not
 * already-placed sales orders. "sales-order" / "facilitator-sales-order" are already-placed orders
 * (that's the existing Sales Orders module's "SO" tab); the actual request types are the ones
 * literally named "-request". Other order_type categories must never appear here, filter or not.
 */
export const ORDER_REQUEST_ORDER_TYPES = ["facilitator-discount-request", "sales-order-request"];

/**
 * GET /order-request/stats — legacy staging-request stats, still used by OrderRequestDashboard.jsx.
 * Not used by the list page anymore (it now reads statusCounts from fetchOrderRequestList instead).
 */
export const fetchOrderRequestStats = createAsyncThunk(
	"orderRequests/fetchOrderRequestStats",
	async ({ channel_id } = {}, { rejectWithValue }) => {
		try {
			const params = new URLSearchParams({
				...(channel_id ? { channel_id } : {}),
			});
			const qs = params.toString() ? `?${params.toString()}` : "";
			const response = await axiosHttp.get(`order-request/stats${qs}`);
			return response.data?.data ?? response.data;
		} catch (err) {
			return rejectWithValue(err?.response?.data?.message || err?.message || "Failed to fetch order request stats");
		}
	}
);

/** GET /orders — placed sales orders, channel-scoped, with per-status counts. */
export const fetchOrderRequestList = createAsyncThunk(
	"orderRequests/fetchOrderRequestList",
	async ({ filters, page = 1, limit = 20, channel_id } = {}, { rejectWithValue }) => {
		try {
			const rangeStart = filters?.date_from || filters?.range?.[0]?.format?.("YYYY-MM-DD") || "";
			const rangeEnd = filters?.date_to || filters?.range?.[1]?.format?.("YYYY-MM-DD") || "";
			const params = new URLSearchParams({
				page,
				limit,
				...(channel_id ? { channel: channel_id } : {}),
				...(filters?.keyword ? { keyword: filters.keyword } : {}),
				...(filters?.status_id ? { status: filters.status_id } : {}),
				order_type: filters?.order_type || ORDER_REQUEST_ORDER_TYPES.join(","),
				...(rangeStart ? { date_from: rangeStart } : {}),
				...(rangeEnd ? { date_to: rangeEnd } : {}),
			});

			const response = await axiosHttp.get(`orders?${params}`);
			return response.data?.data ?? response.data;
		} catch (err) {
			return rejectWithValue(err?.response?.data?.message || err?.message || "Failed to fetch order request list");
		}
	}
);

const OrderRequestListSlice = createSlice({
	name: "OrderRequestListSlice",
	initialState: {
		isLoading: false,
		error: null,
		data: null, // list payload
		stats: null,
		page: 1,
		total: 0,
		isStatsLoading: false,
	},
	extraReducers: (builder) => {
		builder
			.addCase(fetchOrderRequestStats.pending, (state) => {
				state.isStatsLoading = true;
				state.error = null;
			})
			.addCase(fetchOrderRequestStats.fulfilled, (state, action) => {
				state.isStatsLoading = false;
				state.stats = action.payload;
			})
			.addCase(fetchOrderRequestStats.rejected, (state, action) => {
				state.isStatsLoading = false;
				state.error = action.payload || action.error?.message;
			})
			.addCase(fetchOrderRequestList.pending, (state) => {
				state.isLoading = true;
				state.error = null;
			})
			.addCase(fetchOrderRequestList.fulfilled, (state, action) => {
				state.isLoading = false;
				state.data = action.payload;
				state.page = action.payload?.currentPage ?? state.page ?? 1;
				state.total = action.payload?.totalOrdersCount ?? 0;
			})
			.addCase(fetchOrderRequestList.rejected, (state, action) => {
				state.isLoading = false;
				state.error = action.payload || action.error?.message;
			});
	},
});

export default OrderRequestListSlice.reducer;
